import graphql from 'babel-plugin-relay/macro';
import React from 'react';
import {usePaginationFragment, useMutation} from 'react-relay/hooks';
import ConnectionHandler from 'relay-connection-handler-plus';
import useFilters from 'src/hooks/useFilters';
import {showEventNotification} from 'src/utils/notifications';

import Table from '../../../components/table';
import DateCell from '../../../components/table/cell-types/DateCell';
import SecondaryTextCell from '../../../components/table/cell-types/SecondaryTextCell';
import {listPageValveConnectorsQuery} from './__generated__/listPageValveConnectorsQuery.graphql';
import {ValveConnectorsOrder} from './__generated__/ValveConnectorsPaginationQuery.graphql';
import {ValveConnectorsTable_valveConnectors$key} from './__generated__/ValveConnectorsTable_valveConnectors.graphql';
import {ValveConnectorsTableDeleteValveConnectorMutation} from './__generated__/ValveConnectorsTableDeleteValveConnectorMutation.graphql';
import TableFilters from './TableFilters';

type Props = {
  valveConnectors: ValveConnectorsTable_valveConnectors$key;
};

type Filters = {
  name?: string;
  orderBy?: ValveConnectorsOrder;
};

function ValveConnectorsTable(props: Props) {
  const {data, loadNext, hasNext, refetch} = usePaginationFragment<
    listPageValveConnectorsQuery,
    ValveConnectorsTable_valveConnectors$key
  >(
    graphql`
      fragment ValveConnectorsTable_valveConnectors on Query
        @argumentDefinitions(
          first: {type: "Int!"}
          after: {type: "String"}
          name: {type: "String"}
          orderBy: {type: "ValveConnectorsOrder"}
        )
        @refetchable(queryName: "ValveConnectorsPaginationQuery") {
        valveConnectors(first: $first, after: $after, orderBy: $orderBy, name: $name)
          @connection(key: "ValveConnectorsList_valveConnectors") {
          totalCount
          pageInfo {
            hasPreviousPage
            hasNextPage
            startCursor
            endCursor
          }
          edges {
            node {
              id
              createdAt
              name
            }
          }
        }
      }
    `,
    props.valveConnectors,
  );

  const [commit] = useMutation<ValveConnectorsTableDeleteValveConnectorMutation>(
    deleteValveConnectorMutation,
  );

  const {addFilters, setFilters, isTransitioning} = useFilters<Filters>({}, refetch);

  return (
    <Table
      filters={() => (
        <TableFilters onApplyFilters={handleOnApplyFilter} onResetFilters={handleOnResetFilter} />
      )}
      actions={{show: false, delete: true, edit: true, create: true}}
      totalDataLength={data.valveConnectors.totalCount}
      columns={columns}
      isTransitioning={isTransitioning}
      onPaginate={() => hasNext && loadNext(50)}
      onSort={handleOnSort}
      onDelete={handleDelete}
      data={data.valveConnectors.edges}
    />
  );

  function handleOnApplyFilter(filters: {text: string}) {
    addFilters({name: filters.text});
  }

  function handleOnResetFilter() {
    setFilters({});
  }

  async function handleOnSort(sortByConfig: any[]) {
    if (!sortByConfig.length) {
      addFilters({orderBy: undefined});

      return;
    }

    const orderByDirection = sortByConfig[0].desc ? 'DESC' : 'ASC';
    const orderByField = sortByConfig[0].id.toUpperCase();

    addFilters({orderBy: {direction: orderByDirection, field: orderByField}});
  }

  function handleDelete(id: string) {
    commit({
      variables: {input: {valveConnectorId: id}},
      onCompleted: payload => {
        const entry = payload.deleteValveConnector?.valveConnector;

        if (entry) {
          showEventNotification('removed', 'Connector', entry.name);
        }
      },

      updater: store => {
        const root = store.getRoot();

        const connections = ConnectionHandler.getConnections(
          root,
          'ValveConnectorsList_valveConnectors',
        );

        if (!connections) {
          return;
        }

        connections.forEach(connection => {
          ConnectionHandler.deleteNode(connection, id);
        });
      },
    });
  }
}

export default ValveConnectorsTable;

const deleteValveConnectorMutation = graphql`
  mutation ValveConnectorsTableDeleteValveConnectorMutation($input: DeleteValveConnectorInput!) {
    deleteValveConnector(input: $input) {
      valveConnector {
        id
        name
      }
      errors {
        code
        message
      }
    }
  }
`;

const columns = [
  {
    Header: 'Name',
    accessor: 'node.name',
    id: 'name',
    Cell({cell: {value}}: any) {
      return <SecondaryTextCell value={value} />;
    },
  },
  {
    Header: 'Created On',
    accessor: 'node.createdAt',
    id: 'created_at',
    Cell({cell: {value}}: any) {
      return <DateCell value={value} />;
    },
  },
];
